import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { PortalParceiroApi } from './portal-parceiro.api.js';

/**
 * `<erp-portal-trocar-senha-obrigatoria-page>` — tela cheia, mesmo
 * tratamento visual de `<erp-portal-parceiro-login-page>`, única tela
 * alcançável quando `ParceiroAutenticado.deveTrocarSenha` é `true`
 * (guarda em `main.ts`, espelhando o bloqueio real do backend em
 * `middleware/portal-authorization.middleware.ts` — este componente é
 * só a UI, nunca a garantia de segurança). Mesmo padrão estrutural de
 * `erp-trocar-senha-obrigatoria-page` (Auth interno), mas fala com
 * `PortalParceiroApi`, nunca com `AuthApi` — mesma fronteira de
 * identidade já aplicada no resto do Portal.
 */
export class ErpPortalTrocarSenhaObrigatoriaPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _portalApi: PortalParceiroApi | null = null;
  private _aoConcluir: (() => void) | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set portalApi(valor: PortalParceiroApi) {
    this._portalApi = valor;
  }

  set aoConcluir(valor: () => void) {
    this._aoConcluir = valor;
  }

  connectedCallback(): void {
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._portalApi || this.enviando) return;

    const senhaAtual = this.root.querySelector<HTMLInputElement>('#senha-atual')?.value ?? '';
    const novaSenha = this.root.querySelector<HTMLInputElement>('#nova-senha')?.value ?? '';
    const confirmarSenha = this.root.querySelector<HTMLInputElement>('#confirmar-senha')?.value ?? '';

    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      this.mensagemErro = 'preencha todos os campos';
      this.render();
      return;
    }
    if (novaSenha.length < 8) {
      this.mensagemErro = 'a nova senha deve ter ao menos 8 caracteres';
      this.render();
      return;
    }
    if (novaSenha !== confirmarSenha) {
      this.mensagemErro = 'a confirmação não confere com a nova senha';
      this.render();
      return;
    }
    if (novaSenha === senhaAtual) {
      this.mensagemErro = 'a nova senha deve ser diferente da senha atual';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      await this._portalApi.trocarSenhaObrigatoria(senhaAtual, novaSenha);
      this._aoConcluir?.();
    } catch (erro) {
      this.mensagemErro =
        erro instanceof ApiError ? erro.message : 'não foi possível trocar a senha. tente novamente.';
      this.enviando = false;
      this.render();
    }
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          background: var(--pg-color-bg-base, #121417);
          font-family: var(--pg-font-familia-base, system-ui);
        }
        .cartao {
          width: 100%;
          max-width: 380px;
          padding: var(--pg-space-6, 32px);
        }
        .marca {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          font-weight: var(--pg-font-weight-bold, 700);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin-bottom: var(--pg-space-1, 4px);
        }
        .marca span { color: var(--pg-color-dourado, #c9a24b); }
        .subtitulo {
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          margin-bottom: var(--pg-space-2, 8px);
        }
        .aviso {
          font-size: var(--pg-font-size-xs, 0.75rem);
          color: var(--pg-color-alerta, #e0a52c);
          background: rgba(224, 165, 44, 0.1);
          border: var(--pg-border-largura, 1px) solid rgba(224, 165, 44, 0.3);
          border-radius: var(--pg-radius-md, 10px);
          padding: var(--pg-space-3, 12px);
          margin-bottom: var(--pg-space-5, 24px);
          line-height: var(--pg-line-height-base, 1.5);
        }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); }
        ${FORM_FIELD_CSS}
        pg-button { width: 100%; }
        pg-button::part(button) { width: 100%; justify-content: center; }
      </style>
      <pg-card class="cartao">
        <div class="marca">PG <span>Custom</span></div>
        <div class="subtitulo">Portal do Parceiro — troca de senha obrigatória</div>
        <div class="aviso">Sua senha atual é temporária. Defina uma senha nova, só sua, para continuar.</div>
        <form novalidate>
          <label>
            Senha atual (temporária)
            <input id="senha-atual" type="password" autocomplete="current-password" required />
          </label>
          <label>
            Nova senha
            <input id="nova-senha" type="password" autocomplete="new-password" required minlength="8" />
          </label>
          <label>
            Confirmar nova senha
            <input id="confirmar-senha" type="password" autocomplete="new-password" required minlength="8" />
          </label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
          <pg-button variant="primary" ${this.enviando ? 'loading' : ''}>Definir nova senha</pg-button>
        </form>
      </pg-card>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('pg-button')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-portal-trocar-senha-obrigatoria-page', ErpPortalTrocarSenhaObrigatoriaPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-portal-trocar-senha-obrigatoria-page': ErpPortalTrocarSenhaObrigatoriaPage;
  }
}
